/**
 * APTextArea Component
 *
 * A comprehensive text area component following Figma design system specifications.
 * Supports multiple sizes, statuses, resizing, and accessibility features.
 *
 * @example
 * ```tsx
 * <APTextArea
 *   label="Description"
 *   placeholder="Enter description"
 *   hint="Maximum 500 characters"
 *   size="medium"
 *   status="default"
 *   minRows={3}
 *   maxRows={6}
 * />
 *
 * <APTextArea
 *   label="Comments"
 *   placeholder="Add your comments"
 *   status="focus"
 *   clearable
 *   resizable
 * />
 * ```
 */

import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react';

import APTypography from '@shared/components/atoms/typography';
import { useTranslate } from '@shared/hooks/use-translate';

import type { IAPTextAreaProps } from './types';

import './styles.scss';

/**
 * APTextArea - Production-ready textarea component
 *
 * Follows AstroPay design system and accessibility guidelines.
 *
 */
export const APTextArea = forwardRef<HTMLTextAreaElement, IAPTextAreaProps>(
  (
    {
      size = 'medium',
      status = 'default',
      label,
      hint,
      labelExtra,
      clearable = false,
      required = false,
      fullWidth = true,
      resizable = true,
      minRows = 3,
      maxRows,
      className = '',
      'data-testid': testId,
      errorMessage,
      successMessage,
      onClear,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      disabled,
      rows: propRows,
      ...rest
    },
    ref
  ) => {
    const translate = useTranslate();
    // Generate unique ID for accessibility
    const generatedId = useId();
    const inputId = rest.id || generatedId;

    // Internal state for focus management
    const [isFocused, setIsFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(value || defaultValue || '');

    // Ref for the textarea element
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Auto-resize functionality
    const adjustHeight = useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';

      // Calculate new height based on content
      const newHeight = textarea.scrollHeight;

      // Apply min/max row constraints
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 24;
      const minHeight = minRows * lineHeight;
      const maxHeight = maxRows ? maxRows * lineHeight : Infinity;

      const constrainedHeight = Math.min(Math.max(newHeight, minHeight), maxHeight);
      textarea.style.height = `${constrainedHeight}px`;
    }, [minRows, maxRows]);

    // Effect to handle auto-resize when value changes
    useEffect(() => {
      if (textareaRef.current) {
        adjustHeight();
      }
    }, [internalValue, adjustHeight]);

    // Determine actual status based on props and state
    const getActualStatus = () => {
      if (disabled) return 'disabled';
      if (status === 'error' || status === 'success') return status;
      if (isFocused) return 'focus';
      if (internalValue) return 'filled';
      return 'default';
    };

    const actualStatus = getActualStatus();

    // Determine if clear button should be shown
    const showClearButton = clearable && internalValue && !disabled && actualStatus !== 'disabled';

    // Event handlers
    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setIsFocused(true);
        onFocus?.(e);
      },
      [onFocus]
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setIsFocused(false);
        onBlur?.(e);
      },
      [onBlur]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setInternalValue(newValue);
        onChange?.(e);

        // Auto-resize on change
        setTimeout(adjustHeight, 0);
      },
      [onChange, adjustHeight]
    );

    const handleClear = useCallback(() => {
      setInternalValue('');
      if (textareaRef.current) {
        textareaRef.current.value = '';
        adjustHeight();
      }
      onClear?.();
    }, [onClear, adjustHeight]);

    // Construct class names using BEM methodology
    const baseClasses = 'ap-textarea';
    const sizeClasses = `ap-textarea--${size}`;
    const statusClasses = `ap-textarea--${actualStatus}`;
    const stateClasses = [
      fullWidth && 'ap-textarea--full-width',
      resizable && 'ap-textarea--resizable',
    ].filter(Boolean);

    const textAreaClasses = [baseClasses, sizeClasses, statusClasses, ...stateClasses, className]
      .filter(Boolean)
      .join(' ');

    // Determine hint message based on status
    const getHintMessage = () => {
      if (actualStatus === 'error' && errorMessage) return errorMessage;
      if (actualStatus === 'success' && successMessage) return successMessage;
      return hint;
    };

    const hintMessage = getHintMessage();

    // Combine refs
    const combinedRef = useCallback(
      (node: HTMLTextAreaElement) => {
        textareaRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    return (
      <div className={textAreaClasses} data-testid={testId}>
        {/* Label row */}
        {(label || labelExtra) && (
          <div className="ap-textarea__label-row">
            {label && (
              <APTypography
                as="label"
                size="small"
                weight="500"
                color="base"
                className="ap-textarea__label"
                {...{ htmlFor: inputId }}
              >
                {label}
                {required && (
                  <APTypography as="span" color="error" inline>
                    *
                  </APTypography>
                )}
              </APTypography>
            )}
            {labelExtra && (
              <APTypography
                as="span"
                size="small"
                weight="400"
                color="muted"
                className="ap-textarea__label-extra"
              >
                {labelExtra}
              </APTypography>
            )}
          </div>
        )}

        {/* Textarea container */}
        <div className="ap-textarea__input-container">
          {/* Textarea wrapper */}
          <div className="ap-textarea__input-wrapper">
            <textarea
              ref={combinedRef}
              id={inputId}
              className="ap-textarea__input"
              value={value}
              defaultValue={defaultValue}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              required={required}
              rows={propRows || minRows}
              aria-invalid={actualStatus === 'error'}
              aria-required={required}
              aria-describedby={hintMessage ? `${inputId}-hint` : undefined}
              {...rest}
            />
          </div>

          {/* Right container for clear button */}
          {showClearButton && (
            <div className="ap-textarea__right-container">
              <button
                type="button"
                className="ap-textarea__clear-button"
                onClick={handleClear}
                aria-label={translate('aria.clear-field')}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 6L18 18M6 18L18 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Resize handle (if resizable) */}
          {resizable && actualStatus !== 'disabled' && (
            <div className="ap-textarea__resize-handle">
              <svg width="20" height="10" viewBox="0 0 20 10" fill="none">
                <path d="M19 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M19 5L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
          )}
        </div>

        {/* Hint message */}
        {hintMessage && (
          <APTypography
            as="p"
            size="xSmall"
            weight="400"
            color={
              actualStatus === 'error' ? 'error' : actualStatus === 'success' ? 'success' : 'muted'
            }
            className="ap-textarea__hint"
            id={`${inputId}-hint`}
          >
            {hintMessage}
          </APTypography>
        )}
      </div>
    );
  }
);

APTextArea.displayName = 'APTextArea';

export type { IAPTextAreaProps };
export default APTextArea;
